/**
 * OnboardingChecklist — collapsible dashboard card. Six items, each
 * auto-checked by the backend from live data. Disappears permanently
 * once all six are done OR the user clicks the X (backend treats both
 * the same: onboarding_complete = true).
 *
 * Hidden for existing users (onboarding_complete already true server-side).
 */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Progress } from '../ui/progress';
import { Check, ChevronDown, ChevronUp, X, ArrowRight, Circle } from 'lucide-react';
import { useOnboarding } from '../../context/OnboardingContext';

export default function OnboardingChecklist() {
  const { state, dismissChecklist } = useOnboarding();
  const [collapsed, setCollapsed] = useState(false);
  const navigate = useNavigate();

  // Gating: existing users never see this (their onboarding_complete is
  // already true). New users see it until everything is checked off OR
  // they dismiss it.
  if (!state) return null;
  if (state.onboarding_complete) return null;
  if (state.checklist_dismissed) return null;
  if (state.all_checklist_done) return null;

  const checklist = state.checklist || [];
  const completed = checklist.filter((c) => c.done).length;
  const total = checklist.length;
  const pct = total ? Math.round((completed / total) * 100) : 0;

  return (
    <Card
      className="rounded-sm border-2 border-[#E5C57D]/40 bg-gradient-to-br from-[#FAF5E8]/40 to-white"
      data-testid="onboarding-checklist-card"
    >
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <CardTitle className="text-base font-medium text-stone-900">
              Get set up with DH Design
            </CardTitle>
            <div className="flex items-center gap-3 mt-2">
              <Progress
                value={pct}
                className="h-1.5 flex-1 max-w-[180px]"
                data-testid="onboarding-progress"
              />
              <span className="text-xs text-stone-600 shrink-0" data-testid="onboarding-progress-text">
                {completed} of {total} complete
              </span>
            </div>
          </div>
          <div className="flex items-center gap-1 shrink-0">
            <button
              type="button"
              onClick={() => setCollapsed((v) => !v)}
              className="p-1.5 rounded hover:bg-stone-100 text-stone-500"
              aria-label={collapsed ? 'Expand checklist' : 'Collapse checklist'}
              data-testid="onboarding-toggle-btn"
            >
              {collapsed ? <ChevronDown className="h-4 w-4" /> : <ChevronUp className="h-4 w-4" />}
            </button>
            <button
              type="button"
              onClick={() => {
                if (window.confirm("Hide this checklist for good? You won't see it again.")) {
                  dismissChecklist();
                }
              }}
              className="p-1.5 rounded hover:bg-stone-100 text-stone-500"
              aria-label="Dismiss onboarding checklist"
              data-testid="onboarding-dismiss-btn"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
      </CardHeader>
      {!collapsed && (
        <CardContent className="pt-0">
          <ul className="space-y-1.5" data-testid="onboarding-checklist-items">
            {checklist.map((item) => (
              <li
                key={item.key}
                className="flex items-center gap-3 py-1.5 px-2 rounded hover:bg-stone-50 transition-colors"
                data-testid={`onboarding-item-${item.key}`}
              >
                {item.done ? (
                  <span className="shrink-0 w-5 h-5 rounded-full bg-[#1F8A4C] flex items-center justify-center">
                    <Check className="h-3 w-3 text-white" strokeWidth={3} />
                  </span>
                ) : (
                  <Circle className="shrink-0 w-5 h-5 text-stone-300" strokeWidth={1.5} />
                )}
                <span
                  className={`flex-1 text-sm ${
                    item.done ? 'text-stone-400 line-through' : 'text-stone-800'
                  }`}
                >
                  {item.label}
                </span>
                {!item.done && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-xs h-7 text-[#B89B5E] hover:text-[#8E7846] hover:bg-[#FAF5E8]"
                    onClick={() => navigate(item.cta_path)}
                    data-testid={`onboarding-item-cta-${item.key}`}
                  >
                    Start
                    <ArrowRight className="h-3 w-3 ml-1" />
                  </Button>
                )}
              </li>
            ))}
          </ul>
        </CardContent>
      )}
    </Card>
  );
}
